import type { TemplateDefinition } from "./types";

export const T21: TemplateDefinition = {
  id: "t21",
  module: 6,
  number: 21,
  name: "Sentinel Career Advancement Roadmap",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access + Scalable Growth Architecture",
  whyExists:
    "A certified officer has a written plan. This Roadmap is the documented path from LEP-C to the officer's target level with dates, milestones, and upgrade triggers.",
  architectureContext:
    "This template serves the Scalable Growth Architecture pillar. It visualizes your entire Sentinel career path as a documented roadmap — ensuring advancement is intentional, planned, and aligned with your department's promotional track.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Current State Assessment",
      fields: [
        { id: "current_level", type: "text", label: "Current Level", prefill: "L1 LEP-C" },
        { id: "officer_name", type: "text", label: "Officer Name", prefillFrom: "name" },
        { id: "department", type: "text", label: "Department", prefillFrom: "department" },
        { id: "strengths", type: "textarea", label: "Current role strengths relative to the Sentinel framework", placeholder: "Your strengths..." },
        { id: "gaps", type: "textarea", label: "Current gaps the LEP-C is closing", placeholder: "Gaps being addressed..." },
      ],
    },
    {
      id: "s2",
      title: "Section 2 — Target State Definition",
      fields: [
        { id: "target_level", type: "select", label: "Target Sentinel Level", options: [{ label: "L2 LES-C", value: "l2" }, { label: "L3 LEM-C", value: "l3" }, { label: "L4 SLEE-C", value: "l4" }] },
        { id: "target_role", type: "text", label: "Target Role Title", placeholder: "e.g., Sergeant / Patrol Supervisor" },
        { id: "why_target", type: "textarea", label: "Why this is my target", placeholder: "Your rationale..." },
        { id: "career_change", type: "textarea", label: "What changes in my career when I reach this level", placeholder: "What this level unlocks..." },
      ],
    },
    {
      id: "s3",
      title: "Section 3 — Level Milestones",
      variant: "roadmap",
      repeatable: {
        id: "level_milestones",
        label: "Level Milestone",
        maxRows: 4,
        initialRows: 3,
        fields: [
          { id: "level_name", type: "text", label: "Level name and credential", placeholder: "e.g., L2 LES-C" },
          { id: "enroll_date", type: "date", label: "Enrollment target date" },
          { id: "complete_date", type: "date", label: "Completion target date" },
          { id: "status", type: "select", label: "Status", options: [{ label: "Not Started", value: "not_started" }, { label: "Enrolled", value: "enrolled" }, { label: "Complete", value: "complete" }] },
          { id: "competencies", type: "textarea", label: "Key competencies to develop at this level", placeholder: "Competencies..." },
        ],
      },
    },
    {
      id: "s4",
      title: "Section 4 — Timeline",
      variant: "gantt",
      repeatable: {
        id: "timeline_bars",
        label: "Timeline Bar",
        maxRows: 4,
        initialRows: 3,
        fields: [
          { id: "level", type: "text", label: "Level", placeholder: "Level name..." },
          { id: "start", type: "date", label: "Start Date" },
          { id: "end", type: "date", label: "End Date" },
        ],
      },
    },
    {
      id: "s5",
      title: "Section 5 — Organizational Alignment",
      fields: [
        { id: "alignment", type: "textarea", label: "How my Sentinel advancement aligns with my department's promotional track", placeholder: "Your alignment analysis..." },
        { id: "supervisor_aware", type: "radio", label: "My supervisor's awareness of my Sentinel career plan", options: [{ label: "Aware", value: "aware" }, { label: "Not yet discussed", value: "not_discussed" }] },
        { id: "dept_support", type: "select", label: "Department support for Sentinel costs", options: [{ label: "Full reimbursement", value: "full" }, { label: "Partial", value: "partial" }, { label: "None", value: "none" }, { label: "Unknown", value: "unknown" }] },
      ],
    },
  ],
};

export const T22: TemplateDefinition = {
  id: "t22",
  module: 6,
  number: 22,
  name: "2-Year CEC Fulfillment Plan",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access + Scalable Growth Architecture",
  whyExists:
    "Officers who plan their 40 CECs on Day 1 never scramble at the renewal deadline. This plan makes 40 CECs automatic.",
  architectureContext:
    "This template serves the Scalable Growth Architecture pillar. It converts CEC compliance from a deadline scramble into a planned, tracked 24-month timeline with milestone checkpoints and contingency plans.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — CEC Overview",
      fields: [
        { id: "total_cecs", type: "number", label: "Total CECs required", prefill: "40" },
        { id: "renewal_cycle", type: "text", label: "Renewal cycle", prefill: "2 years" },
        { id: "pass_date", type: "date", label: "Credential pass date / renewal start date" },
        { id: "renewal_deadline", type: "date", label: "Renewal deadline (auto-calculated as 2 years from pass date)", autoDateFrom: "pass_date", autoDateOffsetMonths: 24 },
        { id: "cec_per_year", type: "number", label: "CECs per year target", prefill: "20" },
      ],
    },
    {
      id: "s2",
      title: "Section 2 — CEC Sources",
      repeatable: {
        id: "cec_sources",
        label: "CEC Source",
        maxRows: 10,
        initialRows: 2,
        prepopulated: [
          { source_name: "Sentinel Annual Renewal Plan", provider: "Sentinel", cecs_available: "20", cost: "$179/year", planned_date: "", status: "Planned", cecs_earned: "0" },
          { source_name: "Sentinel LES-C Enrollment", provider: "Sentinel", cecs_available: "", cost: "$499", planned_date: "", status: "Planned", cecs_earned: "0" },
        ],
        fields: [
          { id: "source_name", type: "text", label: "Source Name", placeholder: "Source name..." },
          { id: "provider", type: "text", label: "Provider", placeholder: "Provider..." },
          { id: "cecs_available", type: "number", label: "CECs available", placeholder: "0" },
          { id: "cost", type: "text", label: "Cost", placeholder: "Cost..." },
          { id: "planned_date", type: "date", label: "Planned completion date" },
          { id: "status", type: "select", label: "Status", options: [{ label: "Planned", value: "Planned" }, { label: "In Progress", value: "In Progress" }, { label: "Complete", value: "Complete" }] },
          { id: "cecs_earned", type: "number", label: "CECs earned", placeholder: "0" },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Milestone Checkpoints",
      variant: "timeline",
      repeatable: {
        id: "milestones",
        label: "Milestone",
        maxRows: 4,
        initialRows: 4,
        prepopulated: [
          { milestone: "Month 6", target_cecs: "10", actual_cecs: "0", action_if_behind: "" },
          { milestone: "Month 12", target_cecs: "20", actual_cecs: "0", action_if_behind: "" },
          { milestone: "Month 18", target_cecs: "30", actual_cecs: "0", action_if_behind: "" },
          { milestone: "Month 24", target_cecs: "40", actual_cecs: "0", action_if_behind: "" },
        ],
        fields: [
          { id: "milestone", type: "text", label: "Milestone", placeholder: "e.g., Month 6" },
          { id: "target_cecs", type: "number", label: "Target CECs by this milestone", placeholder: "0" },
          { id: "actual_cecs", type: "number", label: "Actual CECs at this milestone", placeholder: "0" },
          { id: "action_if_behind", type: "textarea", label: "Action if behind", placeholder: "Your acceleration plan..." },
        ],
      },
    },
    {
      id: "s4",
      title: "Section 4 — Contingency Plan",
      fields: [
        { id: "reimbursement_lost", type: "textarea", label: "If reimbursement is lost or reduced, my plan to fund CEC completion", placeholder: "Your funding contingency..." },
        { id: "behind_plan", type: "textarea", label: "If I fall behind on CECs with 6 months remaining, my acceleration plan", placeholder: "Your acceleration plan..." },
        { id: "lapse_consequence", type: "textarea", label: "The consequence of a lapsed credential and my commitment to preventing it", placeholder: "Your commitment..." },
      ],
    },
  ],
};

export const T23: TemplateDefinition = {
  id: "t23",
  module: 6,
  number: 23,
  name: "Professional Portfolio Framework",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access + Scalable Growth Architecture",
  whyExists:
    "A promotional board between two equal officers is decided by who has documented their professional impact. This portfolio travels with your credential.",
  architectureContext:
    "This template serves the Scalable Growth Architecture pillar. It creates a portable, documented professional portfolio that travels with your Sentinel credential — giving you a competitive advantage in promotional processes.",
  sections: [
    {
      id: "tab1",
      title: "Tab 1 — Credential Record",
      variant: "tabs",
      repeatable: {
        id: "credentials",
        label: "Credential",
        maxRows: 10,
        initialRows: 1,
        prepopulated: [
          { cred_name: "LEP-C", issuing_org: "Sentinel Certification System", issue_date: "", exp_date: "", status: "Active" },
        ],
        fields: [
          { id: "cred_name", type: "text", label: "Credential Name", placeholder: "Credential..." },
          { id: "issuing_org", type: "text", label: "Issuing Organization", placeholder: "Organization..." },
          { id: "issue_date", type: "date", label: "Issue Date" },
          { id: "exp_date", type: "date", label: "Expiration/Renewal Date" },
          { id: "status", type: "select", label: "Status", options: [{ label: "Active", value: "Active" }, { label: "Expired", value: "Expired" }, { label: "In Progress", value: "In Progress" }] },
        ],
      },
    },
    {
      id: "tab2",
      title: "Tab 2 — Professional Accomplishments",
      variant: "tabs",
      repeatable: {
        id: "accomplishments",
        label: "Accomplishment",
        maxRows: 20,
        initialRows: 1,
        addLabel: "+ Add Accomplishment",
        fields: [
          { id: "title", type: "text", label: "Title", placeholder: "e.g., Reduced vehicle break-ins by 18% in Meridian Heights corridor" },
          { id: "date", type: "text", label: "Date or period", placeholder: "e.g., Q3 2024" },
          { id: "situation", type: "textarea", label: "Situation: what was the problem", placeholder: "The problem..." },
          { id: "action", type: "textarea", label: "Action: what I did specifically", placeholder: "Your action..." },
          { id: "result", type: "textarea", label: "Result: measurable outcome", placeholder: "Measurable outcome..." },
          { id: "doc_ref", type: "text", label: "Supporting documentation reference", placeholder: "Reference..." },
        ],
      },
    },
    {
      id: "tab3",
      title: "Tab 3 — Community Engagement Record",
      variant: "tabs",
      repeatable: {
        id: "engagement",
        label: "Engagement",
        maxRows: 20,
        initialRows: 1,
        addLabel: "+ Add Engagement",
        fields: [
          { id: "program", type: "text", label: "Program or Initiative", placeholder: "Program..." },
          { id: "segment", type: "text", label: "Community Segment", placeholder: "Community..." },
          { id: "role", type: "text", label: "My Role", placeholder: "Your role..." },
          { id: "period", type: "text", label: "Period", placeholder: "e.g., Jan 2024 - Present" },
          { id: "outcome", type: "textarea", label: "Measurable outcome", placeholder: "Outcome..." },
        ],
      },
    },
    {
      id: "tab4",
      title: "Tab 4 — Training and Development Record",
      variant: "tabs",
      repeatable: {
        id: "training",
        label: "Training",
        maxRows: 20,
        initialRows: 1,
        addLabel: "+ Add Training",
        fields: [
          { id: "training_name", type: "text", label: "Training Name", placeholder: "Training..." },
          { id: "provider", type: "text", label: "Provider", placeholder: "Provider..." },
          { id: "hours", type: "text", label: "Hours/CECs", placeholder: "e.g., 8 hrs / 2 CECs" },
          { id: "date_completed", type: "date", label: "Date Completed" },
          { id: "skills", type: "text", label: "Skills developed", placeholder: "Skills..." },
        ],
      },
    },
    {
      id: "tab5",
      title: "Tab 5 — Leadership Contributions",
      variant: "tabs",
      repeatable: {
        id: "leadership",
        label: "Leadership Contribution",
        maxRows: 20,
        initialRows: 1,
        addLabel: "+ Add Contribution",
        fields: [
          { id: "type", type: "select", label: "Contribution Type", options: [{ label: "Mentoring", value: "mentoring" }, { label: "Training Delivery", value: "training_delivery" }, { label: "Policy Input", value: "policy_input" }, { label: "Committee", value: "committee" }, { label: "Other", value: "other" }] },
          { id: "description", type: "textarea", label: "Description", placeholder: "Description..." },
          { id: "period", type: "text", label: "Period", placeholder: "e.g., 2024" },
          { id: "impact", type: "textarea", label: "Impact", placeholder: "Impact..." },
        ],
      },
    },
  ],
};

export const T24: TemplateDefinition = {
  id: "t24",
  module: 6,
  number: 24,
  name: "LES-C Readiness Assessment and L2 Enrollment Plan",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access + Scalable Growth Architecture",
  whyExists:
    "A certified L1 officer has already assessed their readiness for L2 before the renewal date — not after. This is the structured transition tool.",
  architectureContext:
    "This template serves the Scalable Growth Architecture pillar. It provides a structured readiness assessment and enrollment plan for advancing from LEP-C to LES-C — ensuring the transition is intentional and well-timed.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Competency Gap Analysis",
      repeatable: {
        id: "competency_gaps",
        label: "Competency Area",
        maxRows: 5,
        initialRows: 5,
        prepopulated: [
          { area: "Supervisory Performance Management", rating: "3", evidence: "", gap: "" },
          { area: "Use-of-Force Supervision", rating: "3", evidence: "", gap: "" },
          { area: "Supervisory Legal Compliance", rating: "3", evidence: "", gap: "" },
          { area: "Incident Command", rating: "3", evidence: "", gap: "" },
          { area: "Team Development", rating: "3", evidence: "", gap: "" },
        ],
        fields: [
          { id: "area", type: "text", label: "Competency area", placeholder: "Area..." },
          { id: "rating", type: "slider", label: "Self-rating (1=No experience, 3=Some exposure, 5=Ready to certify)", min: 1, max: 5, step: 1 },
          { id: "evidence", type: "textarea", label: "Evidence of current competency", placeholder: "Your evidence..." },
          { id: "gap", type: "textarea", label: "Gap to close before L2", placeholder: "Your gap..." },
        ],
      },
    },
    {
      id: "s2",
      title: "Section 2 — Experience Validation",
      fields: [
        { id: "supervisory_exp", type: "textarea", label: "Supervisory experience I currently have that supports L2 content", placeholder: "Your supervisory experience..." },
        { id: "fto_roles", type: "textarea", label: "Field training, mentoring, or team lead roles I have held", placeholder: "Your FTO/mentoring roles..." },
        { id: "supervisory_incidents", type: "textarea", label: "Incidents or projects where I functioned at a supervisory level", placeholder: "Your supervisory-level incidents..." },
      ],
    },
    {
      id: "s3",
      title: "Section 3 — Timing Assessment",
      fields: [
        { id: "ready_date", type: "date", label: "My assessment of when I am ready to enroll in LES-C" },
        { id: "trigger", type: "textarea", label: "The career event or milestone that will trigger my enrollment", placeholder: "Your enrollment trigger..." },
        { id: "enroll_reason", type: "radio", label: "Am I enrolling to prepare for a role or to confirm readiness for a role I am already in?", options: [{ label: "Preparing for future role", value: "preparing" }, { label: "Confirming current role readiness", value: "confirming" }] },
      ],
    },
    {
      id: "s4",
      title: "Section 4 — L2 Enrollment Plan",
      fields: [
        { id: "target_enroll_date", type: "date", label: "My target LES-C enrollment date" },
        { id: "upgrade_info", type: "textarea", label: "How I upgrade from LEP-C to LES-C within the Sentinel system", prefill: "No external application required. Plan switching is enabled immediately upon completing LEP-C. Log in to your Sentinel account and select Upgrade to L2." },
        { id: "budget_plan", type: "textarea", label: "Budget and cost plan for LES-C ($499)", placeholder: "Your budget plan..." },
        { id: "first_action", type: "textarea", label: "The first action I will take toward L2 enrollment", placeholder: "Your first action..." },
      ],
    },
  ],
};
